import { useEffect, useRef, useState } from 'react';
import { analyzeProduct } from '@/lib/api';
import type { ProductSilhouette } from '@/mocks/app-data';
import StepHeader from './StepHeader';

interface StepDetailsProps {
  productFile: File | null;
  productImage: string;
  productName: string;
  productDescription: string;
  onProductNameChange: (name: string) => void;
  onProductDescriptionChange: (description: string) => void;
  /** Fashion route only — surfaced when analyze-product classifies the
   *  upload as a top/bottoms/shoes garment. Parent uses this to skip the
   *  manual Garment Type step and route to the matching mask on /generate. */
  onGarmentTypeDetected?: (g: 'top' | 'bottoms' | 'shoes' | 'none') => void;
  /** Fashion route only — surfaced when analyze-product flags the upload
   *  as restricted content (swimwear / lingerie / underwear / sheer).
   *  Parent uses this to block Continue and surface the warning. */
  onGarmentRestrictionDetected?: (r: 'swimwear' | 'lingerie' | 'underwear' | 'sheer' | 'none') => void;
  /** Product Shot route only — fires when analyze-product detects a
   *  human / model in the uploaded image. Parent uses this to show a
   *  soft warning suggesting the Fashion route. Non-blocking. */
  onHumanModelDetected?: (hasModel: boolean) => void;
  /** Product Shot route — surfaced when analyze-product classifies the
   *  upload's product category + vibe tags. Parent uses these to populate
   *  the "Recommended for you" group on Step 5 (Background). */
  onProductInsightsDetected?: (insights: { category?: string; tags?: string[]; silhouette?: ProductSilhouette }) => void;
  /** Fashion route only — when true, this step renders the garment-type
   *  warning if analyze-product returns 'none', and the parent's
   *  canProceed() blocks Continue until the user re-uploads. Defaults
   *  false; other routes don't care about garmentType. */
  requireGarment?: boolean;
  /** Latest classification from analyze-product (or parent state). Drives
   *  the warning banner. Optional; only consulted when requireGarment. */
  garmentType?: 'top' | 'bottoms' | 'shoes' | 'none' | null;
  /** Latest restriction flag from analyze-product. When set to anything
   *  other than 'none', renders the restricted-content warning instead
   *  of the not-a-garment one and blocks Continue. Only consulted when
   *  requireGarment. */
  garmentRestriction?: 'swimwear' | 'lingerie' | 'underwear' | 'sheer' | 'none' | null;
  /** Product Shot route only — when true, this step renders a soft
   *  warning suggesting the Fashion route if analyze-product reports
   *  a human/model in the uploaded image. Non-blocking; canProceed()
   *  does NOT gate on this. Defaults false; other routes don't care. */
  warnHumanModel?: boolean;
  /** Latest human-model flag from analyze-product. Optional; only
   *  consulted when warnHumanModel. */
  hasHumanModel?: boolean | null;
  onCostAdd: (delta: number) => void;
  /** When true, skip the StepHeader. /appv2's Section wrapper supplies
   *  its own header so the inner one would be a duplicate. */
  compact?: boolean;
  /** Multi-product mode (carousel route with 2+ products). When this
   *  prop is set with `files.length > 1`, the step renders one card
   *  per product instead of the single-product two-column layout.
   *  Each card has its own image preview + name + description fields
   *  and runs its own analyze-product call on mount if name+desc are
   *  empty (manual upload case). Library + URL picks pre-fill these
   *  arrays so analyze is skipped.
   *
   *  The single-product props above (productFile / productImage /
   *  productName / productDescription / onProductNameChange / etc.)
   *  are ignored when multiProducts is set. Fashion-specific
   *  warnings (requireGarment, garmentRestriction) and Product Shot
   *  hasHumanModel warnings are also skipped in multi mode — those
   *  routes don't use carousel. */
  multiProducts?: {
    files: File[];
    images: string[];
    names: string[];
    descriptions: string[];
    onNameChange: (idx: number, value: string) => void;
    onDescriptionChange: (idx: number, value: string) => void;
  };
}

export default function StepDetails({
  productFile,
  productImage,
  productName,
  productDescription,
  onProductNameChange,
  onProductDescriptionChange,
  onGarmentTypeDetected,
  onGarmentRestrictionDetected,
  onHumanModelDetected,
  onProductInsightsDetected,
  requireGarment = false,
  garmentType,
  garmentRestriction,
  warnHumanModel = false,
  hasHumanModel,
  onCostAdd,
  compact,
  multiProducts,
}: StepDetailsProps) {
  // Multi-product carousel branch — bail to the per-product card stack
  // before any single-product hooks/state run. Multi mode skips all the
  // single-product analyze logic + fashion/product-shot warnings; each
  // card runs its own independent analyze if needed. Note: hooks below
  // this early return are still called unconditionally (the conditional
  // is on RENDER, not on hook calls — multiProducts is read-only across
  // a render).
  if (multiProducts && multiProducts.files.length > 1) {
    return (
      <MultiProductDetailsView
        compact={compact}
        files={multiProducts.files}
        images={multiProducts.images}
        names={multiProducts.names}
        descriptions={multiProducts.descriptions}
        onNameChange={multiProducts.onNameChange}
        onDescriptionChange={multiProducts.onDescriptionChange}
        onCostAdd={onCostAdd}
      />
    );
  }
  return <SingleProductDetailsView
    productFile={productFile}
    productImage={productImage}
    productName={productName}
    productDescription={productDescription}
    onProductNameChange={onProductNameChange}
    onProductDescriptionChange={onProductDescriptionChange}
    onGarmentTypeDetected={onGarmentTypeDetected}
    onGarmentRestrictionDetected={onGarmentRestrictionDetected}
    onHumanModelDetected={onHumanModelDetected}
    onProductInsightsDetected={onProductInsightsDetected}
    requireGarment={requireGarment}
    garmentType={garmentType}
    garmentRestriction={garmentRestriction}
    warnHumanModel={warnHumanModel}
    hasHumanModel={hasHumanModel}
    onCostAdd={onCostAdd}
    compact={compact}
  />;
}

// =============================================================================
// SingleProductDetailsView — the original StepDetails behavior, extracted so
// we can early-return to MultiProductDetailsView for the carousel case
// without nesting all the hook calls under a conditional. Same code that
// shipped pre-May-27; props identical to the StepDetails signature.
// =============================================================================
function SingleProductDetailsView({
  productFile,
  productImage,
  productName,
  productDescription,
  onProductNameChange,
  onProductDescriptionChange,
  onGarmentTypeDetected,
  onGarmentRestrictionDetected,
  onHumanModelDetected,
  onProductInsightsDetected,
  requireGarment = false,
  garmentType,
  garmentRestriction,
  warnHumanModel = false,
  hasHumanModel,
  onCostAdd,
  compact,
}: Omit<StepDetailsProps, 'multiProducts'>) {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Tracks which File instance the auto-analyze has already run against
  // (or is currently running against). The Fashion route may bounce the
  // user back to Step 2 to re-upload after a 'none' classification, and
  // when they return we need to re-classify the *new* file even though
  // productName/Description still hold the prior value.
  const analyzedFileRef = useRef<File | null>(null);

  const runAnalysis = async () => {
    if (!productFile) return;
    setAnalyzing(true);
    setError(null);
    try {
      const result = await analyzeProduct(productFile);
      if (result.name) onProductNameChange(result.name);
      if (result.description) onProductDescriptionChange(result.description);
      if (result.garmentType && onGarmentTypeDetected) {
        onGarmentTypeDetected(result.garmentType);
      }
      if (onGarmentRestrictionDetected) {
        // Always fire even when 'none' so the parent can clear a stale
        // restriction set by a prior file. Old backends that don't return
        // the field at all (during a deploy window) get treated as
        // 'none' = unrestricted.
        onGarmentRestrictionDetected(result.garmentRestriction ?? 'none');
      }
      if (onHumanModelDetected) {
        // Same pattern — always fire so parent state syncs to the latest
        // file's classification. Old backend missing the field → false.
        onHumanModelDetected(result.hasHumanModel ?? false);
      }
      if (onProductInsightsDetected && (result.productCategory || result.tags || result.productSilhouette)) {
        onProductInsightsDetected({
          category: result.productCategory,
          tags: result.tags,
          silhouette: result.productSilhouette,
        });
      }
      onCostAdd(result._cost ?? 0);
    } catch (err) {
      console.error('Auto-fill failed:', err);
      setError(err instanceof Error ? err.message : 'Could not analyze image');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (!productFile) return;
    // Skip if we've already analyzed THIS file. handleUpload creates a
    // fresh File array on every upload, so a new upload of even the
    // same-named file produces a different reference and fires fresh.
    if (analyzedFileRef.current === productFile) return;
    // Metadata-present skip: name + description already came from the
    // library pick OR the URL scrape (both carry full metadata + cached
    // classification). Skip the expensive vision analyze-product call
    // entirely — the user vouched for the product when they added it.
    //
    // This is unconditional across routes per the May 26 design
    // change. Previously fashion (requireGarment) AND product-shot
    // (warnHumanModel) bypassed this skip-guard so they could detect
    // garmentType / hasHumanModel from vision. That bypass was dropped
    // because:
    //   - Library/URL picks were ALREADY safety-vetted at add-time —
    //     re-running on every wizard entry is wasteful + slow.
    //   - Manual file uploads (no name/desc yet) still trigger
    //     analyze normally — vision still runs when there's no
    //     metadata to trust, which is the case the gate was designed
    //     for.
    if (productName || productDescription) {
      analyzedFileRef.current = productFile;
      return;
    }
    analyzedFileRef.current = productFile;
    runAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productFile, requireGarment, warnHumanModel]);

  return (
    // /app: h-full flex column inside the fixed-viewport wizard panel.
    // /appv2 (compact): no height constraint — content flows in the
    // page's natural scroll.
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {/* Header */}
      {!compact && (
        <StepHeader
          icon="ri-sparkling-2-line"
          title="Tell us about your product"
          subtitle="We've auto-detected what your product is. Confirm or edit so the AI can write better copy."
        />
      )}

      {/* Two-column body */}
      {/* md:items-start stops the grid from stretching both columns to the
          same height — without it, the image panel grows/shrinks based on
          the right column's content (loading spinner, error banner, garment
          warnings, etc), which the user perceived as "panel changes size
          per image". With items-start, each column sizes independently
          from its own content. */}
      <div className={`${compact ? '' : 'flex-1 min-h-0'} grid grid-cols-1 md:grid-cols-2 gap-5 md:items-start`}>
        {/* Product image — locked to square aspect so the panel is the
            same size regardless of the uploaded image's natural ratio.
            Image scales within via object-contain (always fully visible,
            never cropped). */}
        <div className="rounded-2xl bg-white border border-surface-dark/40 p-4 flex items-center justify-center overflow-hidden aspect-square w-full max-w-md mx-auto md:mx-0">
          {productImage ? (
            <img
              src={productImage}
              alt="Product"
              className="max-w-full max-h-full object-contain rounded-xl"
              draggable={false}
            />
          ) : (
            <p className="text-dark-muted text-sm">No product image uploaded</p>
          )}
        </div>

        {/* Fields */}
        <div className="flex flex-col gap-4 min-h-0">
          {analyzing && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-primary/10 text-primary text-sm font-medium border border-primary/20">
              <i className="ri-sparkling-2-line" />
              <div className="w-4 h-4 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
              <span>Analyzing your product…</span>
            </div>
          )}

          {error && !analyzing && (
            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              <span className="flex-1">{error}</span>
              <button
                onClick={runAnalysis}
                className="px-2 py-1 rounded-md text-xs font-bold text-red-700 hover:bg-red-100 transition-colors cursor-pointer"
              >
                Retry
              </button>
            </div>
          )}

          {/* Fashion route only — analyze-product flagged the upload as
              restricted content (swimwear / lingerie / underwear / sheer).
              These get rejected by OpenAI's content policy at /generate
              time, and they're off-brand for the model templates we
              ship. Surface a clear block here so the user doesn't waste
              the round-trip. Takes priority over the not-a-garment
              warning (a restricted item IS a garment). */}
          {requireGarment && !analyzing && garmentRestriction && garmentRestriction !== 'none' && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-800 text-sm font-medium border border-red-200">
              <i className="ri-shield-cross-line mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold mb-0.5">
                  We can&apos;t process this type of garment.
                </p>
                <p className="text-red-700 text-xs leading-relaxed">
                  {restrictionMessage(garmentRestriction)} Please upload a
                  regular top, bottoms, or shoes photo instead, or switch
                  to a different category on Step&nbsp;1.
                </p>
              </div>
            </div>
          )}

          {/* Fashion route only — analyze-product classified the upload as
              not-a-garment. Block Continue (handled in page.tsx canProceed)
              and tell the user to swap in a top, bottoms, or shoes photo. */}
          {requireGarment && !analyzing && garmentType === 'none' && (!garmentRestriction || garmentRestriction === 'none') && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 text-amber-800 text-sm font-medium border border-amber-200">
              <i className="ri-alert-line mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold mb-0.5">
                  This doesn&apos;t look like a clothing item.
                </p>
                <p className="text-amber-700 text-xs leading-relaxed">
                  Fashion templates only support tops, bottoms, or shoes. Go
                  back a step and upload one of those, or pick a different
                  category from Step&nbsp;1.
                </p>
              </div>
            </div>
          )}

          {/* Product Shot route only — analyze-product detected a human /
              model in the upload. Product Shot composes the product into
              a lifestyle scene (kitchen counter, marble surface, etc.)
              which gives weird results when the "product" is a person.
              Now BLOCKING — page.tsx canProceed() gates Continue when
              postType === 'product-shot' && hasHumanModel === true.
              User must either re-upload a standalone product photo or
              switch to the Fashion category on Step 1. */}
          {warnHumanModel && !analyzing && hasHumanModel && (
            <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 text-amber-800 text-sm font-medium border border-amber-200">
              <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold mb-0.5">
                  Product Shot doesn't accept photos with people.
                </p>
                <p className="text-amber-700 text-xs leading-relaxed">
                  This category is for standalone product shots — shoes,
                  watches, jewelry, fragrances, candles, beauty. For a
                  garment on a model, switch to the <strong>Fashion</strong>{' '}
                  category on Step&nbsp;1; for an in-context lifestyle
                  shot, switch to <strong>Carousel</strong>. Otherwise,
                  re-upload a product-only photo to continue.
                </p>
              </div>
            </div>
          )}

          <div>
            <label htmlFor="product-name" className="block text-sm font-semibold text-dark mb-2">
              Product <span className="text-red-500">*</span>
            </label>
            <input
              id="product-name"
              type="text"
              value={productName}
              onChange={(e) => onProductNameChange(e.target.value)}
              placeholder="e.g., Lavender Body Cream"
              disabled={analyzing}
              className="w-full px-4 py-3 rounded-xl border border-dark-muted/40 bg-white text-dark text-base placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:opacity-60"
            />
          </div>

          <div>
            <label htmlFor="product-description" className="block text-sm font-semibold text-dark mb-2">
              Description
            </label>
            <input
              id="product-description"
              type="text"
              value={productDescription}
              onChange={(e) => onProductDescriptionChange(e.target.value)}
              placeholder="A short sentence about it…"
              disabled={analyzing}
              className="w-full px-4 py-3 rounded-xl border border-dark-muted/40 bg-white text-dark text-base placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:opacity-60"
            />
          </div>

          {!analyzing && (productName || productDescription) && (
            <button
              onClick={runAnalysis}
              className="self-start inline-flex items-center gap-2 px-4 py-2 rounded-full border border-surface-dark hover:border-primary/40 text-dark-muted hover:text-primary text-sm font-medium transition-colors cursor-pointer"
            >
              <i className="ri-sparkling-2-line" />
              Re-analyze with AI
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// MultiProductDetailsView — carousel-route stack of one card per product.
// Renders N independent MultiProductCard components, each with its own
// auto-analyze logic for files lacking name+description metadata.
// Fashion-specific warnings + Product-Shot human-model warnings are NOT
// surfaced here — carousel route doesn't need them.
// =============================================================================
function MultiProductDetailsView({
  compact,
  files,
  images,
  names,
  descriptions,
  onNameChange,
  onDescriptionChange,
  onCostAdd,
}: {
  compact?: boolean;
  files: File[];
  images: string[];
  names: string[];
  descriptions: string[];
  onNameChange: (idx: number, value: string) => void;
  onDescriptionChange: (idx: number, value: string) => void;
  onCostAdd: (delta: number) => void;
}) {
  return (
    <div className={compact ? '' : 'h-full flex flex-col'}>
      {!compact && (
        <StepHeader
          icon="ri-sparkling-2-line"
          title="Tell us about each product"
          subtitle="Each carousel frame uses a different product — confirm or edit the details below so the AI can write tailored copy."
        />
      )}
      <div className={`${compact ? '' : 'flex-1 min-h-0'} flex flex-col gap-3`}>
        {files.map((file, idx) => (
          <MultiProductCard
            key={`${file.name}-${file.size}-${idx}`}
            index={idx}
            file={file}
            image={images[idx] ?? ''}
            name={names[idx] ?? ''}
            description={descriptions[idx] ?? ''}
            onNameChange={(v) => onNameChange(idx, v)}
            onDescriptionChange={(v) => onDescriptionChange(idx, v)}
            onCostAdd={onCostAdd}
          />
        ))}
      </div>
    </div>
  );
}

// =============================================================================
// MultiProductCard — one row in the multi-product stack. Compact horizontal
// layout: small image thumbnail on the left, name + description fields
// stacked on the right. Runs its own analyze-product call on mount IF the
// passed name+description are both empty (manual file upload case).
// Library / URL picks arrive pre-populated → analyze is skipped.
// =============================================================================
function MultiProductCard({
  index,
  file,
  image,
  name,
  description,
  onNameChange,
  onDescriptionChange,
  onCostAdd,
}: {
  index: number;
  file: File;
  image: string;
  name: string;
  description: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onCostAdd: (delta: number) => void;
}) {
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Same skip-if-already-ran pattern as SingleProductDetailsView. Per-File
  // identity tracking: a new File ref (from re-upload) re-triggers analyze.
  const analyzedFileRef = useRef<File | null>(null);

  const runAnalysis = async () => {
    if (!file) return;
    setAnalyzing(true);
    setError(null);
    try {
      const result = await analyzeProduct(file);
      if (result.name) onNameChange(result.name);
      if (result.description) onDescriptionChange(result.description);
      onCostAdd(result._cost ?? 0);
    } catch (err) {
      console.error('Auto-fill failed (multi-product card):', err);
      setError(err instanceof Error ? err.message : 'Could not analyze image');
    } finally {
      setAnalyzing(false);
    }
  };

  useEffect(() => {
    if (!file) return;
    if (analyzedFileRef.current === file) return;
    // Same metadata-skip logic as the single-product path: if name OR
    // description is already populated, the user (or scrape, or
    // library pick) provided it — skip the vision call.
    if (name || description) {
      analyzedFileRef.current = file;
      return;
    }
    analyzedFileRef.current = file;
    runAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  return (
    <div className="rounded-2xl bg-white border border-surface-dark/40 p-3 flex gap-3 items-start">
      {/* Position badge + thumbnail — primary-tinted ribbon over the
          tile so the user sees frame mapping at a glance. */}
      <div className="relative w-20 h-20 rounded-xl overflow-hidden bg-surface/40 border border-surface-dark/30 flex-shrink-0">
        {image ? (
          <img
            src={image}
            alt={`Product ${index + 1}`}
            className="w-full h-full object-contain p-1.5"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-dark-muted/40">
            <i className="ri-image-line text-xl" />
          </div>
        )}
        <div className="absolute top-1 left-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-md">
          <span className="text-[10px] font-black leading-none">{index + 1}</span>
        </div>
      </div>

      {/* Fields */}
      <div className="flex-1 min-w-0 flex flex-col gap-2">
        {analyzing && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-primary/10 text-primary text-xs font-medium border border-primary/20">
            <div className="w-3 h-3 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
            <span>Analyzing…</span>
          </div>
        )}
        {error && !analyzing && (
          <div className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-red-50 text-red-700 text-xs font-medium border border-red-200">
            <i className="ri-error-warning-line text-sm" />
            <span className="flex-1">{error}</span>
            <button
              onClick={runAnalysis}
              className="px-1.5 py-0.5 rounded text-[10px] font-bold text-red-700 hover:bg-red-100 transition-colors cursor-pointer"
            >
              Retry
            </button>
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold text-dark-muted uppercase tracking-wider mb-0.5">
            Product <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => onNameChange(e.target.value)}
            placeholder="e.g., Lavender Body Cream"
            disabled={analyzing}
            className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:opacity-60"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold text-dark-muted uppercase tracking-wider mb-0.5">
            Description
          </label>
          <input
            type="text"
            value={description}
            onChange={(e) => onDescriptionChange(e.target.value)}
            placeholder="A short sentence about it…"
            disabled={analyzing}
            className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all disabled:opacity-60"
          />
        </div>
      </div>
    </div>
  );
}

function restrictionMessage(r: 'swimwear' | 'lingerie' | 'underwear' | 'sheer' | 'none'): string {
  switch (r) {
    case 'swimwear':
      return 'Swimwear (bikinis, swimsuits, board shorts) isn’t supported by the model-try-on pipeline.';
    case 'lingerie':
      return 'Lingerie items aren’t supported by the model-try-on pipeline.';
    case 'underwear':
      return 'Underwear isn’t supported by the model-try-on pipeline.';
    case 'sheer':
      return 'Sheer or see-through items aren’t supported by the model-try-on pipeline.';
    default:
      return '';
  }
}
